<template>
    <div class="w-full lg:h-[380px] aspect-video">
        <div class="flex gap-[16px] h-full">
            <div class="flex-1 rounded-[12px] relative overflow-hidden" 
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd">
                <div
                    v-if="loadedMain.length < mainList.length"
                    class="absolute inset-0 bg-off-white animate-pulse z-0"
                />
                <a
                    v-for="(item, index) in mainList"
                    :key="index"
                    :href="item.link"
                    :title="item.title"
                    :class="[getCardClass(+index), 'absolute inset-0 shadow-base']"
                    >
                    <picture>
                        <source :srcset="item.image.webp" type="image/webp" />
                        <img
                        :src="item.image.source"
                        alt="image"
                        data-sizes="auto"
                        class="h-full w-full object-cover lazyload group-hover:scale-105 transition-all duration-700"
                        :class="{ 'opacity-100': loadedMain.includes(+index), 'opacity-0': !loadedMain.includes(+index) }"
                        @load="loadedMain.push(+index)"
                        />
                    </picture>
                    <!-- Text Wrapper -->
                    <div v-if="loadedMain.includes(+index)" class="absolute inset-[32px] sm:inset-[40px]  flex flex-col gap-[1rem] justify-center z-10 text-white">
                        <h1 class="block m-0 text-[18px] sm:text-[38px] font-medium leading-normal" v-html="item.title"></h1>
                        <p class="block text-[14px] sm:text-[22px] font-medium leading-normal" v-html="item.sub_title"></p>
                        <!-- <a v-if="item.link" :href="item.link" :title="item.link_title" role="button" class="hidden sm:block w-max border border-white text-white px-[28px] py-[14px] rounded-[10px] pointer-events-auto hover:bg-white hover:text-charcoal transition-all leading-[20px]">
                            {{ item.link_title }}
                        </a> -->
                    </div>
                </a>

                <div
                class="absolute left-1/2 -translate-x-1/2 bottom-[16px] px-[16px] py-[6px] flex gap-[4px] rounded-[15px] bg-charcoal/[20%] z-20 transition duration-500"
                :class="[loadedMain.length < mainList.length ? 'translate-y-[200%] opacity-0' : 'translate-y-[0%] opacity-100']"
                >
                <div
                    v-for="(_, index) in mainList.length"
                    :key="index"
                    class="h-[8px] w-[8px] rounded-full transition-all duration-700"
                    :class="{
                        'bg-lime-green': index === activeIndex,
                        'cursor-pointer hover:bg-lime-green bg-white/[50%]': index !== activeIndex,
                        
                    }"
                    @click="activeIndex = index"
                />
                </div>
            </div>
            <!-- extras -->
            <div v-if="!fullWidth" class="hidden w-1/3 h-full lg:flex flex-col gap-[16px]">
                <div class="bg-off-white rounded-[12px] overflow-hidden h-full relative block group">
                    <div v-if="!loaded?.top" class="absolute inset-0 bg-off-white animate-pulse z-0"/>
                    <a v-if="extraTop" :href="extraTop.link" :title="extraTop.title">
                        <picture>
                            <source :srcset="extraTop.image.webp" type="image/webp">
                            <img :src="extraTop.image.source"
                                alt="image"
                                data-sizes="auto"
                                class="h-full w-full object-cover lazyload group-hover:scale-105 transition-all duration-700"
                                :class="loaded?.top ? 'opacity-100' : 'opacity-0'"
                                @load="loaded.top = true"
                            >
                        </picture>
                        <!-- Text Wrapper -->
                        <div v-if="loaded?.bottom" class="absolute inset-[24px] flex flex-col justify-end z-10 text-white pointer-events-none">
                            <h2 v-if="extraTop.title" class="block m-0 text-[22px] font-semibold leading-[28px]" v-html="extraTop.title"/>
                            <p v-if="extraTop.sub_title" class="block text-[22px] font-semibold leading-[28px]" v-html="extraTop.sub_title"/>
                        </div>
                        <div class="absolute inset-0 z-[9] bg-gradient-to-b from-white/0 to-charcoal/10"/>
                    </a>
                </div>
                <div class="rounded-[12px] overflow-hidden h-full relative block group">
                    <div v-if="!loaded?.bottom" class="absolute inset-0 bg-off-white animate-pulse z-0"/>
                    <a v-if="extraBottom" :href="extraBottom.link" :title="extraBottom.title">
                        <picture>
                            <source :srcset="extraBottom.image.webp" type="image/webp">
                            <img :src="extraBottom.image.source"
                                alt="image"
                                data-sizes="auto"
                                class="h-full w-full object-cover lazyload group-hover:scale-105 transition-all duration-700"
                                :class="loaded?.bottom ? 'opacity-100' : 'opacity-0'"
                                @load="loaded.bottom = true"
                            >
                        </picture>
                        <!-- Text Wrapper -->
                        <div v-if="loaded?.bottom" class="absolute inset-[24px] flex flex-col justify-end z-10 text-white pointer-events-none">
                            <h2 v-if="extraBottom.title" class="block m-0 text-[22px] font-semibold leading-[28px]" v-html="extraBottom.title"/>
                            <p v-if="extraBottom.sub_title" class="block text-[22px] font-semibold leading-[28px]" v-html="extraBottom.sub_title"/>
                        </div>
                        <div class="absolute inset-0 z-[9] bg-gradient-to-b from-white/0 to-charcoal/10"/>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    main: {
        type: Object,
        required: true,
    },
    extras: {
        type: Object,
        required: true,
    },
    fullWidth: {
        type: Boolean,
        default: true
    }
})
const swipeThreshold = 50;
const loadedMain = reactive([])
const loaded = reactive({})
const activeIndex = ref(0)
const previousActiveIndex = ref(0);
const direction = ref('next');
const isInitial = ref(true);
let autoplayInterval = null;

// Autoplay Function
function startAutoplay() {
  autoplayInterval = setInterval(() => {
    goToNextSlide();
  }, 1000 * 5); // Change slide every 5 seconds
}

function stopAutoplay() {
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
    autoplayInterval = null;
  }
}

// Initialize autoplay on mount and clean up on unmount
onMounted(() => {
  setTimeout(() => {
    startAutoplay();
  }, 10 * 1000);
});

onBeforeUnmount(() => {
  stopAutoplay();
});

// Watch activeIndex to reset autoplay timer
watch(activeIndex, () => {
  stopAutoplay();
  startAutoplay();
});

watch(activeIndex, (newVal, oldVal) => {
  isInitial.value = false;
  previousActiveIndex.value = oldVal;
  direction.value = newVal > oldVal ? 'next' : 'prev';
});

const mainList = computed(() => {
    return Object.values(props.main)
})
const extraTop = computed(()=>{
    return Object.values(props.extras)[0]
})
const extraBottom = computed(()=>{
    return Object.values(props.extras)[1]
})

function getCardClass(index) {
  if (isInitial.value && index === activeIndex.value) {
    return 'z-[5] translate-x-[0%]';
  }
  if (index === activeIndex.value) {
    return direction.value === 'next' ? 'z-[5] slide-in-left' : 'z-[5] slide-in-right';
  } else if (index === previousActiveIndex.value) {
    return 'z-0 translate-x-[0%]';
  } else {
    return 'hidden';
  }
}

//touch support
const touchStartX = ref(0);
const touchEndX = ref(0);

function handleTouchStart(event) {
  touchStartX.value = event.touches[0].clientX;
}

function handleTouchMove(event) {
  touchEndX.value = event.touches[0].clientX;
}

function handleTouchEnd() {
  // Calculate the horizontal movement distance
  const swipeDistance = touchEndX.value - touchStartX.value;

  // Only trigger a swipe if the distance exceeds the threshold
  if (swipeDistance < -swipeThreshold) {
    goToNextSlide(); // Swipe Left
  } else if (swipeDistance > swipeThreshold) {
    goToPreviousSlide(); // Swipe Right
  }
}

function goToNextSlide() {
  if (activeIndex.value < mainList.value.length - 1) {
    activeIndex.value += 1;
  } else {
    activeIndex.value = 0; // Loop back to the first slide
  }
}

function goToPreviousSlide() {
  if (activeIndex.value > 0) {
    activeIndex.value -= 1;
  } else {
    activeIndex.value = mainList.value.length - 1; // Loop to the last slide
  }
}

</script>

<style lang="scss" scoped>
.slide-in-left {
    animation: slideInLeft 0.5s forwards;
}

@keyframes slideInLeft {
    from {
        transform: translateX(100%);
    }

    to {
        transform: translateX(0%);
    }
}

.slide-in-right {
    animation: slideInRight 0.5s forwards;
}

@keyframes slideInRight {
    from {
        transform: translateX(-100%);
    }

    to {
        transform: translateX(0%);
    }
}
</style>